/**
 * StageConfirmationModal — Step E (Phase 1) + Admin Polish Pack (2026-03)
 *
 * Instructor records a verdict on a pupil's stage. Reads owner-locked copy
 * from /api/learner-framework/stage-confirmation/labels so wording cannot
 * drift from the locked tone.
 *
 * Verdicts:
 *   • ready         → cert auto-issued, next stage unlocks.
 *                     Gated by the "Release Next Stage" panel — a written
 *                     justification (min 30 chars) AND at least one readiness
 *                     check are required before submit.
 *   • nearly_there  → carry-forward note appears in next-brief
 *   • not_yet       → carry-forward note appears in next-brief
 *
 * Tone rules (server-enforced):
 *   clarity without blame · progress without shame ·
 *   positive next action  · confidence through proof
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  CircleDashed,
  RefreshCw,
  X,
  ArrowRight,
  AlertTriangle,
  Sparkles,
  Shield,
  TrendingUp,
  ListChecks,
} from "lucide-react";
import useAsyncAction from "../../hooks/useAsyncAction";
import { MobileDialog } from "../mobile";
import LayerPromotionRail from "../training/LayerPromotionRail";

const API = process.env.REACT_APP_BACKEND_URL;

const VERDICT_TONE = {
  ready: {
    ring: "ring-emerald-500",
    bg: "bg-emerald-50 hover:bg-emerald-100",
    text: "text-emerald-900",
    Icon: CheckCircle2,
  },
  nearly_there: {
    ring: "ring-amber-500",
    bg: "bg-amber-50 hover:bg-amber-100",
    text: "text-amber-900",
    Icon: RefreshCw,
  },
  not_yet: {
    ring: "ring-slate-500",
    bg: "bg-slate-50 hover:bg-slate-100",
    text: "text-slate-900",
    Icon: CircleDashed,
  },
};

const SIGNAL_TONE = {
  green: {
    ring: "ring-emerald-400",
    chipBg: "bg-emerald-100",
    chipText: "text-emerald-800",
    dot: "bg-emerald-500",
    label: "Green — indicators aligned",
  },
  amber: {
    ring: "ring-amber-400",
    chipBg: "bg-amber-100",
    chipText: "text-amber-900",
    dot: "bg-amber-500",
    label: "Amber — some indicators still in flight",
  },
  red: {
    ring: "ring-rose-400",
    chipBg: "bg-rose-100",
    chipText: "text-rose-800",
    dot: "bg-rose-500",
    label: "Red — repeated issues flagged",
  },
};

export default function StageConfirmationModal({
  open,
  onClose,
  pupilId,
  pupilFirstName,
  stageSlug, // optional; if omitted, modal asks instructor to pick
  onConfirmed,
}) {
  const [labels, setLabels] = useState(null);
  const [chosenStage, setChosenStage] = useState(stageSlug || "");
  const [verdict, setVerdict] = useState(null);
  const [note, setNote] = useState("");
  const [justification, setJustification] = useState("");
  const [readinessChecks, setReadinessChecks] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  // Shared async-action contract — blocks double-tap on submit and refetches.
  const { run, busy: submitting } = useAsyncAction();

  useEffect(() => {
    if (!open) return;
    setVerdict(null);
    setNote("");
    setJustification("");
    setReadinessChecks([]);
    setReadiness(null);
    setChosenStage(stageSlug || "");
    axios
      .get(`${API}/api/learner-framework/stage-confirmation/labels`, {
        withCredentials: true,
      })
      .then((r) => setLabels(r.data))
      .catch(() => toast.error("Couldn't load confirmation copy."));
  }, [open, stageSlug]);

  // Fetch the release-readiness panel whenever the instructor picks "Ready"
  // on a known stage. Safe to re-fetch if they flip stage or verdict.
  useEffect(() => {
    if (!open || verdict !== "ready" || !chosenStage || !pupilId) return;
    setReadinessLoading(true);
    axios
      .get(
        `${API}/api/learner-framework/stages/${chosenStage}/release-readiness`,
        { params: { pupil_id: pupilId }, withCredentials: true }
      )
      .then((r) => setReadiness(r.data))
      .catch(() => toast.error("Couldn't load readiness indicators."))
      .finally(() => setReadinessLoading(false));
  }, [open, verdict, chosenStage, pupilId]);

  if (!open) return null;

  const stages = labels?.stages || [];
  const verdicts = labels?.verdicts || [];
  const carry = labels?.carry_forward || {};
  const readyMode = verdict === "ready";
  const minJustified = justification.trim().length >= 30;
  const atLeastOneCheck = readinessChecks.length > 0;
  const canSubmitReady = minJustified && atLeastOneCheck;

  const toggleCheck = (id) => {
    setReadinessChecks((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const submit = () => {
    if (!chosenStage) {
      toast.error("Pick a stage to confirm.");
      return;
    }
    if (!verdict) {
      toast.error("Choose Ready, Nearly there, or Not yet.");
      return;
    }
    if (readyMode && !canSubmitReady) {
      toast.error(
        "Add a written justification and tick at least one readiness check."
      );
      return;
    }
    const payload = {
      pupil_id: pupilId,
      verdict,
      note: note.trim() || undefined,
    };
    if (readyMode) {
      payload.justification = justification.trim();
      payload.readiness_checks = readinessChecks;
    }
    return run(
      () =>
        axios.post(
          `${API}/api/learner-framework/stages/${chosenStage}/confirmation`,
          payload,
          { withCredentials: true }
        ),
      {
        errorMessage:
          "Couldn't save the confirmation — please try again.",
        onSuccess: async (data) => {
          const tone =
            verdict === "ready"
              ? "Next stage released — cert issued and progression unlocked."
              : verdict === "nearly_there"
              ? "Carry-forward saved — next lesson will surface a fresh approach."
              : "Carry-forward saved — staying on this stage with a positive next try.";
          toast.success(tone);
          onConfirmed?.(data);
          onClose();
        },
      }
    );
  };

  const signalTone = readiness?.readiness?.signal
    ? SIGNAL_TONE[readiness.readiness.signal]
    : null;

  return (
    <MobileDialog
      bareContent
      open
      onClose={onClose}
      testid="stage-confirmation-modal"
      presentation="center"
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl max-w-2xl w-full mx-3"
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-3 sticky top-0 bg-white dark:bg-slate-900 z-10">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-amber-600">
              {readyMode ? "Release next stage" : "Confirm a stage"}
            </p>
            <h2 className="text-lg font-extrabold text-slate-900 dark:text-white mt-0.5">
              {pupilFirstName ? `${pupilFirstName}'s ` : ""}stage check
            </h2>
            <p className="text-[12px] text-slate-500 mt-1 leading-snug">
              A coaching decision, never a pass/fail.{" "}
              {readyMode
                ? "Progression is earned — justify why this learner is ready."
                : "Builds the learner's Passport."}
            </p>
          </div>
          <button
            data-testid="stage-confirmation-close"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 flex-none"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Stage picker (only if not pre-selected) */}
          {!stageSlug && (
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5">
                Which stage?
              </label>
              <select
                data-testid="stage-confirmation-stage-select"
                value={chosenStage}
                onChange={(e) => setChosenStage(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold text-slate-900 dark:text-white"
              >
                <option value="">— Select stage —</option>
                {stages.map((s) => (
                  <option key={s.slug} value={s.slug}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Verdict picker */}
          <div className="space-y-2">
            <p className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">
              Where is this learner today?
            </p>
            {verdicts.map((v) => {
              const tone = VERDICT_TONE[v.id] || VERDICT_TONE.not_yet;
              const isSel = verdict === v.id;
              return (
                <button
                  key={v.id}
                  data-testid={`stage-confirmation-verdict-${v.id}`}
                  onClick={() => setVerdict(v.id)}
                  className={`w-full text-left rounded-xl px-4 py-3 ring-2 transition flex items-start gap-3 ${tone.bg} ${tone.text} ${
                    isSel ? tone.ring : "ring-transparent"
                  }`}
                >
                  <tone.Icon className="w-5 h-5 flex-none mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold">{v.label}</p>
                    <p className="text-[12px] mt-0.5 opacity-90 leading-snug">
                      {v.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* ── RELEASE NEXT STAGE PANEL (ready verdict only) ─────── */}
          {readyMode && (
            <div
              data-testid="release-next-stage-panel"
              className="space-y-4 pt-1"
            >
              {readinessLoading && (
                <div
                  data-testid="release-readiness-loading"
                  className="rounded-xl bg-slate-50 dark:bg-slate-800 p-4 text-[12px] text-slate-500"
                >
                  Loading readiness indicators…
                </div>
              )}

              {readiness && (
                <>
                  {/* Current stage overview */}
                  <section
                    data-testid="release-current-stage-overview"
                    className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                          Current stage
                        </p>
                        <p className="text-[15px] font-extrabold text-slate-900 dark:text-white leading-tight">
                          {readiness.current_stage.label}
                        </p>
                      </div>
                      <span className="flex-none rounded-full bg-slate-100 dark:bg-slate-800 text-[11px] font-extrabold px-2.5 py-1 text-slate-700 dark:text-slate-200">
                        {readiness.current_stage.responsibility_pct}% responsibility
                      </span>
                    </div>
                    {readiness.current_stage.focus_areas?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mt-2">
                          Key focus
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          {readiness.current_stage.focus_areas.map((f, i) => (
                            <li
                              key={i}
                              className="text-[12.5px] text-slate-700 dark:text-slate-200"
                            >
                              • {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {readiness.current_stage.success_criteria?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mt-2">
                          What success looks like
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          {readiness.current_stage.success_criteria.map(
                            (s, i) => (
                              <li
                                key={i}
                                className="text-[12.5px] text-slate-700 dark:text-slate-200"
                              >
                                ✓ {s}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </section>

                  {/* Readiness indicators */}
                  <section
                    data-testid="release-readiness-indicators"
                    className={`rounded-xl border p-4 space-y-2 ${
                      signalTone
                        ? `border-transparent ring-2 ${signalTone.ring} bg-white dark:bg-slate-900`
                        : "border-slate-200 dark:border-slate-700"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-slate-500" />
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                        Readiness indicators
                      </p>
                      {signalTone && (
                        <span
                          data-testid={`release-signal-${readiness.readiness.signal}`}
                          className={`ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold ${signalTone.chipBg} ${signalTone.chipText}`}
                        >
                          <span
                            className={`w-2 h-2 rounded-full ${signalTone.dot}`}
                          />
                          {signalTone.label}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2.5">
                        <p className="text-[10px] font-bold uppercase text-slate-500">
                          Stage lessons
                        </p>
                        <p className="text-sm font-extrabold text-slate-900 dark:text-white mt-0.5">
                          {readiness.current_stage.completed_count}/
                          {readiness.current_stage.lesson_count} ·{" "}
                          {readiness.readiness.lesson_completion_pct}%
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2.5">
                        <p className="text-[10px] font-bold uppercase text-slate-500">
                          Latest verdict on stage
                        </p>
                        <p className="text-sm font-extrabold text-slate-900 dark:text-white mt-0.5 capitalize">
                          {(
                            readiness.readiness.last_verdict_on_stage || "—"
                          ).replace("_", " ")}
                        </p>
                      </div>
                    </div>

                    {readiness.readiness.ai_analysis && (
                      <div
                        data-testid="release-ai-tone"
                        className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-900/40 p-2.5 flex gap-2"
                      >
                        <Sparkles className="w-4 h-4 text-indigo-500 flex-none mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold uppercase text-indigo-700 dark:text-indigo-300">
                            Latest AI analysis · tone{" "}
                            {readiness.readiness.ai_analysis.tone}
                          </p>
                          <p className="text-[12px] text-slate-700 dark:text-slate-200 mt-0.5 line-clamp-3">
                            {readiness.readiness.ai_analysis.summary}
                          </p>
                        </div>
                      </div>
                    )}

                    {readiness.readiness.recent_carry_forwards?.length > 0 && (
                      <div
                        data-testid="release-carry-forwards"
                        className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 p-2.5"
                      >
                        <p className="text-[10px] font-bold uppercase text-amber-700 dark:text-amber-300">
                          Recent carry-forwards
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          {readiness.readiness.recent_carry_forwards.map(
                            (c, i) => (
                              <li
                                key={i}
                                className="text-[12px] text-slate-700 dark:text-slate-200"
                              >
                                • {c.stage_slug} ·{" "}
                                <span className="capitalize">
                                  {(c.verdict || "").replace("_", " ")}
                                </span>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}

                    {readiness.readiness.repeated_issue_skills?.length > 0 && (
                      <div
                        data-testid="release-repeated-issues"
                        className="rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-900/40 p-2.5 flex gap-2"
                      >
                        <AlertTriangle className="w-4 h-4 text-rose-500 flex-none mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold uppercase text-rose-700 dark:text-rose-300">
                            Repeated issues
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            {readiness.readiness.repeated_issue_skills.map(
                              (r, i) => (
                                <li
                                  key={i}
                                  className="text-[12px] text-slate-700 dark:text-slate-200"
                                >
                                  • {r.skill} — {r.count} lessons running
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </section>

                  {/* Doctrine snapshot */}
                  <section
                    data-testid="release-doctrine-snapshot"
                    className="rounded-xl bg-slate-900 text-slate-100 p-4"
                  >
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-amber-400" />
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-amber-400">
                        Stay aligned — the method
                      </p>
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {readiness.doctrine.map((d, i) => (
                        <li key={i} className="text-[12.5px]">
                          <span className="font-extrabold text-white">
                            {d.principle}.
                          </span>{" "}
                          <span className="text-slate-300">{d.supporting}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Next stage preview */}
                  {readiness.next_stage && (
                    <section
                      data-testid="release-next-stage-preview"
                      className="rounded-xl border-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-4"
                    >
                      <div className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-emerald-600" />
                        <p className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                          Next stage preview
                        </p>
                        <span className="ml-auto inline-flex items-center rounded-full bg-emerald-600 text-white text-[11px] font-extrabold px-2 py-0.5">
                          +{readiness.next_stage.responsibility_delta}%
                          responsibility
                        </span>
                      </div>
                      <p className="mt-2 text-sm font-extrabold text-slate-900 dark:text-white leading-tight">
                        {readiness.next_stage.label}
                      </p>
                      {readiness.next_stage.introduces && (
                        <p className="text-[12.5px] text-slate-700 dark:text-slate-200 mt-1">
                          <span className="font-bold">Introduces: </span>
                          {readiness.next_stage.introduces}
                        </p>
                      )}
                      {readiness.next_stage.new_skills?.length > 0 && (
                        <ul className="mt-2 space-y-0.5">
                          {readiness.next_stage.new_skills.map((s, i) => (
                            <li
                              key={i}
                              className="text-[12px] text-slate-700 dark:text-slate-200"
                            >
                              → {s}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )}

                  {/* B0 Step 3 — calm-promotion rail. Owner-locked copy
                      served by the backend so wording can't drift. Shown
                      on the Release Next Stage panel so the instructor
                      can frame the next lesson before releasing. */}
                  <LayerPromotionRail
                    note={readiness.layer_promotion_note}
                    variant="instructor"
                  />

                  {/* Instructor input — REQUIRED */}
                  <section className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <ListChecks className="w-4 h-4 text-slate-500" />
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                        Instructor input · required
                      </p>
                    </div>
                    <div>
                      <label className="block text-[12px] font-extrabold text-slate-700 dark:text-slate-200 mb-1.5">
                        Why is this learner ready?
                      </label>
                      <textarea
                        data-testid="release-justification"
                        value={justification}
                        onChange={(e) =>
                          setJustification(e.target.value.slice(0, 500))
                        }
                        placeholder="e.g. Held calm at crawling pace for three reversing drills, mirrors are now purposeful, took feedback between attempts."
                        rows={3}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm text-slate-900 dark:text-white resize-none"
                      />
                      <div className="flex justify-between text-[10px] mt-1">
                        <span
                          className={
                            minJustified
                              ? "text-emerald-600 font-bold"
                              : "text-slate-400"
                          }
                        >
                          {minJustified
                            ? "✓ Justification ready"
                            : `Min 30 chars · ${justification.trim().length}/30`}
                        </span>
                        <span className="text-slate-400">
                          {justification.length}/500
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      {(readiness.readiness_checks || []).map((opt) => {
                        const ticked = readinessChecks.includes(opt.id);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            data-testid={`release-check-${opt.id}`}
                            onClick={() => toggleCheck(opt.id)}
                            className={`w-full text-left rounded-lg px-3 py-2 flex items-center gap-2 text-[13px] font-bold transition ${
                              ticked
                                ? "bg-emerald-50 dark:bg-emerald-900/20 ring-2 ring-emerald-500 text-emerald-900 dark:text-emerald-100"
                                : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100"
                            }`}
                          >
                            <span
                              className={`w-4 h-4 rounded border flex items-center justify-center flex-none ${
                                ticked
                                  ? "bg-emerald-500 border-emerald-500 text-white"
                                  : "border-slate-400"
                              }`}
                            >
                              {ticked && <CheckCircle2 className="w-3 h-3" />}
                            </span>
                            {opt.label}
                          </button>
                        );
                      })}
                      {!atLeastOneCheck && (
                        <p className="text-[10px] text-slate-400 mt-1">
                          Tick at least one — confirms which readiness
                          signals you've seen in person.
                        </p>
                      )}
                    </div>
                  </section>
                </>
              )}
            </div>
          )}

          {/* Optional note (always visible — non-ready carry-forward) */}
          {!readyMode && verdict && (
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5">
                What didn't land? (optional)
              </label>
              <textarea
                data-testid="stage-confirmation-note"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 280))}
                placeholder="e.g. Pinch points still feel rushed — try the two-question rule next time."
                rows={3}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm text-slate-900 dark:text-white resize-none"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {note.length}/280 · Surfaces in next lesson's Work Mode brief.
              </p>
            </div>
          )}

          {/* Ready-mode short note (kept optional — justification is the primary) */}
          {readyMode && (
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5">
                What proof points held? (optional note for the Passport)
              </label>
              <textarea
                data-testid="stage-confirmation-note"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 280))}
                placeholder="e.g. Three calm real-road right turns using the mirror reference."
                rows={2}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm text-slate-900 dark:text-white resize-none"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {note.length}/280 · Appears on the learner's Passport.
              </p>
            </div>
          )}

          {/* Tone rules — visible reminder for carry-forward verdicts */}
          {(verdict === "nearly_there" || verdict === "not_yet") && carry.prompt && (
            <div
              data-testid="stage-confirmation-carry-forward-preview"
              className="rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-900/40 p-3"
            >
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-600 dark:text-indigo-400">
                Next lesson will see:
              </p>
              <p className="text-[13px] font-bold text-slate-900 dark:text-white mt-1">
                {carry.prompt}
              </p>
              <p className="text-[12px] text-slate-600 dark:text-slate-300 mt-0.5">
                {carry.supporting_line}
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex gap-2 sticky bottom-0 bg-white dark:bg-slate-900">
          <button
            data-testid="stage-confirmation-cancel"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-11 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold text-sm hover:bg-slate-200"
          >
            Cancel
          </button>
          <button
            data-testid="stage-confirmation-submit"
            onClick={submit}
            disabled={
              submitting ||
              !verdict ||
              !chosenStage ||
              (readyMode && !canSubmitReady)
            }
            className="flex-1 h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed disabled:hover:bg-slate-200 text-white font-extrabold text-sm transition-colors inline-flex items-center justify-center gap-2"
          >
            {submitting
              ? "Saving…"
              : readyMode
              ? (
                <>
                  Release next stage
                  <ArrowRight className="w-4 h-4" />
                </>
              )
              : "Save confirmation"}
          </button>
        </div>
      </div>
    </MobileDialog>
  );
}
